/* eslint-disable semi */
import { useState, useEffect } from 'react'
import { Controller } from 'react-hook-form'

import Button from '@/elements/Button'
import Form from '@/elements/Form'
import Loading from '@/elements/Loading'
import FormFile from '@/elements/FormFile'
import Flex from '@/elements/Flex'
import FormInput from '@/elements/FormInput'
import FormSelect from '@/elements/FormSelect'
import { statesData, type StateData } from '@/utils/stateData'
import useRoles from '@/hooks/useRoles'
import type useOrganizationMemberForm from '@/hooks/forms/useOrganizationMemberForm'
import { OrganizationMemberRolesWithoutOrgLead } from '@/api/types/Organization'
import { useGetOrganizationDetails } from '@/api/organization'

function OrganizationMemberForm({
  form,
  mode,
  onSubmit,
}: {
  form: ReturnType<typeof useOrganizationMemberForm>['form'];
  mode: 'add' | 'edit';
  onSubmit: () => void;
}) {
  const [emailEditable, setEmailEditable] = useState(false)
  const { getRoleName } = useRoles()
  const { data: organizationData, isLoading: organizationDataLoading } =
    useGetOrganizationDetails()
  const organizationName =
    organizationData?.data?.organizationDetails?.name || ''

  // Reset editable state once the email field is no longer dirty (e.g. after save)
  useEffect(() => {
    if (!form.formState.dirtyFields.email) {
      setEmailEditable(false)
    }
  }, [form.formState.dirtyFields.email])

  const handleCancelEmail = () => {
    form.resetField('email')
    setEmailEditable(false)
  };

  return (
    <>
      {organizationDataLoading ? (
        <Flex align='center' justify='center' wfull py={8}>
          <Loading />
        </Flex>
      ) : (
        <Form
          onSubmit={e => {
            e.preventDefault()
            onSubmit()
          }}
        >
          <Controller
            name='avatar'
            control={form.control}
            render={({ field }) => (
              <FormFile
                {...field}
                files={field.value ? [field.value] : []}
                label='Profile Image'
                placeholder='Upload Photo'
                accept='.png, .jpg, .jpeg'
                onChange={e => {
                  const file = e.target.files?.[0]
                  field.onChange(file)
                }}
                onClear={() => {
                  form.setValue('avatar', null)
                  form.setValue('previousAvatar', '')
                  field.onChange(null)
                }}
                error={form.formState.errors.avatar?.message}
                instructions='JPEG or PNG files supported.'
                preview='avatar'
                previousImageUrl={form.watch('previousAvatar') as string}
              />
            )}
          />
          <Flex className='form-group'>
            <Controller
              name='firstName'
              control={form.control}
              render={({ field }) => (
                <FormInput
                  {...field}
                  label='First Name'
                  placeholder='Enter first name'
                  dirty={!!form.formState.dirtyFields.firstName}
                  error={form.formState.errors.firstName?.message as string}
                />
              )}
            />
            <Controller
              name='lastName'
              control={form.control}
              render={({ field }) => (
                <FormInput
                  {...field}
                  label='Last Name'
                  placeholder='Enter last name'
                  dirty={!!form.formState.dirtyFields.lastName}
                  error={form.formState.errors.lastName?.message as string}
                />
              )}
            />
          </Flex>
            <Flex
              wfull
              inline
              wrap
              gap={3}
              align='flex-end'
            >
              <Controller
                name='email'
                control={form.control}
                render={({ field }) => (
                    <>
                    <div style={{ flex: 1 }}>
                      <FormInput
                        {...field}
                        type='email'
                        label='Email'
                        placeholder='Enter email'
                        dirty={!!form.formState.dirtyFields.email}
                        error={form.formState.errors.email?.message as string}
                        disabled={mode === 'edit' && !emailEditable}
                      />
                    </div>
                      {mode === 'edit' &&
                        (emailEditable ? (
                          <Button
                            variant='secondary'
                            onClick={handleCancelEmail}
                          >
                            Cancel
                          </Button>
                        ) : (
                          <Button
                            variant='secondary'
                            onClick={() => setEmailEditable(true)}
                          >
                            Change
                          </Button>
                        ))}
                        </>
                )}
              />
            </Flex>
          <Flex className='form-group'>
            <Controller
              name='city'
              control={form.control}
              render={({ field }) => (
                <FormInput
                  {...field}
                  label='City Location'
                  placeholder='Enter city name'
                  dirty={!!form.formState.dirtyFields.city}
                  error={form.formState.errors.city?.message}
                />
              )}
            />
            <Controller
              name='state'
              control={form.control}
              render={({ field }) => (
                <FormSelect<StateData>
                  {...field}
                  label='State Location'
                  placeholder='Select state'
                  options={statesData}
                  optionValue={item => item.state}
                  optionLabel={item => `${item.state} (${item.stateCode})`}
                  onChange={([val]) => field.onChange(val ? val.state : '')}
                  values={
                    field.value
                      ? statesData.filter(s => s.state === field.value)
                      : []
                  }
                  dirty={!!form.formState.dirtyFields.state}
                  error={form.formState.errors.state?.message}
                  filterBy={(item, search) =>
                    item.state.toLowerCase().includes(search.toLowerCase())
                  }
                />
              )}
            />
          </Flex>
          <Flex className='form-group'>
            <FormInput
              name='organization'
              label='Organization'
              value={organizationName}
              disabled
            />
            <Controller
              name='role'
              control={form.control}
              render={({ field }) => (
                <FormSelect
                  {...field}
                  label='Role'
                  placeholder='Select role'
                  values={field.value ? [field.value] : []}
                  optionValue={item => item}
                  optionLabel={item => getRoleName(item)}
                  options={OrganizationMemberRolesWithoutOrgLead}
                  onChange={val => field.onChange(val[0] || '')}
                  dirty={!!form.formState.dirtyFields.role}
                  error={form.formState.errors.role?.message}
                />
              )}
            />
          </Flex>
          {mode === 'edit' && (
            <Flex className='form-controls'>
              <Button
                submit
                variant='accent'
                disabled={!form.formState.isDirty}
              >
                Save changes
              </Button>
            </Flex>
          )}
        </Form>
      )}
    </>
  )
}
export default OrganizationMemberForm
